import { FlightingService } from "../../../ServiceDesk/sdServices/flightingSvc";
import { UiControlConstants } from "../../common/uicontrol-constants";

export class SelectMenuBinding {
  /** The name to display for the end user as clickable text to open the control, used as the accessiblity label for glyph select menus.  Can be transcluded with a `display-name` element in the transclusion body. */
  public displayName = "@";

  /** The aria name for the end user to open the control, used as the accessiblity label for glyph select menus.  Can be transcluded with a `display-name` element in the transclusion body. */
  public setAriaLabel = "@";

  /** The type of select menu-- "text" and "glyph" are both supported. */
  public displayType = "@";

  /** The list of menu items to bind to. It's expected that this list will conform to the SelectMenuItem class type. */
  public menuItems = "=?";

  /** Callback raised when a menu item is selected. The callback argument is { menuItem: SelectMenuItem }. */
  public menuSelected = "&?";

  /** Callback raised when the menu itself is clicked. The callback argument is { name: string }. */
  public menuClicked = "&?";

  /** Can be set to true to increase the size of the dropdown arrow. False by default. */
  public largeArrow = "@?";

  /** Callback raised when the menu is opened or closed. The callback argument is { wasOpened: boolean }. */
  public onOpenedChanged = "&?";

  /** If true, the right side of the menu will be aligned with the right side of the control. Otherwise, it aligns left. */
  public alignMenuRight = "@?";

  /** The identifier for all business intelligence (BI) actions triggered by this component.*/
  public biId = "@?";

  /** Boolean to set position:fixed.*/
  public fixed = "<?";
}

/** Represents a menu item that appears inside of a select menu. */
export interface SelectMenuItem {
  /** name which will be displayed in the menu */
  itemName: string;
  /** Underlying value for the given key value pair */
  itemValue: any;
  /** The UI automation id for the item. Will be bound one-time. */
  automationId?: string;
}

export class SelectMenu {
  public menuItems: Array<SelectMenuItem> | {};
  public menuSelected: (arg: { menuItem: SelectMenuItem }) => void;
  public menuClicked: (arg: { name: string }) => void;
  public displayType: string;
  public displayName: string;
  public toggleMenu: boolean = false;
  public largeArrow: boolean;
  public alignMenuRight: boolean;
  private onOpenedChanged: (argument: { wasOpened: boolean }) => void;
  public focusedItemIndex = 0;
  private menuItemClass = ".clickable-item";
  public isCustomActionMenuFlightingEnabled: boolean;
  // @ngInject
  constructor(
    public $transclude: any,
    public $scope: ng.IScope,
    public $element: ng.IAugmentedJQuery,
    public $timeout: ng.ITimeoutService,
    public uiControlConstants: UiControlConstants,
    private flightingService: FlightingService
  ) {
    this.isCustomActionMenuFlightingEnabled = this.flightingService.isFlightEnabled("CASE_CUSTOM_LAYOUT");
  }

  /**
   * Fired when a menuitem is clicked and is passed across to the controller
   * @param  {SelectMenuItem} item which is selected
   */
  public selectItem(item: SelectMenuItem) {
    let preventIt = false;
    let eventObject = {
      menuItem: item,
      preventDefault: function () {
        preventIt = true;
      }
    };
    if (this.menuSelected) {
      this.menuSelected(eventObject);
    }

    if (!preventIt) {
      this.toggleMenu = !this.toggleMenu;
    }
  }

  /**
   * closes the drop down menu of the control when clicked outside.
   */
  public collapseSelect($event: ng.IAngularEvent, clickedOutside: boolean) {
    this.toggleMenu = false;
    if (this.onOpenedChanged) {
      this.onOpenedChanged({ wasOpened: this.toggleMenu });
    }
  }

  /**
   * occurs before the menu appears so that we can change items can be changed/edited
   */
  public menuDisplay() {
    let eventObject = { name: this.displayName };
    if (this.menuClicked) {
      this.menuClicked(eventObject);
    }

    this.toggleMenu = !this.toggleMenu;
    if (this.toggleMenu) {
      // set focus to first menu item on menu being displayed
      this.$timeout(() => {
        this.$element.find(this.menuItemClass).first().focus();
      }, 0);
    }

    if (this.onOpenedChanged) {
      this.onOpenedChanged({ wasOpened: this.toggleMenu });
    }
  }

  public navigateDropDown($event: any, item: SelectMenuItem) {
    let expectedKeyCodes = true;
    switch ($event.keyCode) {
      case this.uiControlConstants.keyCode.upArrowKeyCode:
        this.navigateToPreviousMenuItem($event.target);
        break;
      case this.uiControlConstants.keyCode.downArrowKeyCode:
        this.navigateToNextMenuItem($event.target);
        break;
      case this.uiControlConstants.keyCode.spaceKeyCode:
      case this.uiControlConstants.keyCode.enterKeyCode:
        this.selectItem(item);
        break;
      case this.uiControlConstants.keyCode.escapeKeyCode:
        this.toggleMenu = false;
        break;
      default:
        expectedKeyCodes = false;
        break;
    }

    if (expectedKeyCodes) {
      $event.preventDefault();
      $event.stopPropagation();
    }
  }

  public navigateToNextMenuItem(target: any) {
    $(target).parent().next().find(this.menuItemClass).focus();
  }

  public navigateToPreviousMenuItem(target: any) {
    $(target).parent().prev().find(this.menuItemClass).focus();
  }
}

class SelectMenuReplace {
  private selectMenu: SelectMenu;
  // @ngInject
  constructor(private $scope: ng.IScope, private $element: ng.IRootElementService) {}

  $onInit() {
    if (this.selectMenu.$transclude.isSlotFilled("itemTemplate")) {
      let newScope = this.selectMenu.$scope.$new();
      (<any>newScope).item = this.$scope.$parent.item;
      this.selectMenu.$transclude(
        newScope,
        (content: any) => {
          this.$element.replaceWith(content);
        },
        undefined,
        "itemTemplate"
      );
    }
  }
}
export const sdSelectMenu: ng.IComponentOptions = {
  controller: SelectMenu,
  templateUrl: "../ShellCommon/Controls/selectmenu/selectmenu.html",
  transclude: {
    itemTemplate: "?itemTemplate",
    displayName: "?displayName"
  },
  bindings: <any>new SelectMenuBinding()
};

export const sdSelectMenuReplaceWithTransclusion: ng.IComponentOptions = {
  controller: SelectMenuReplace,
  require: {
    selectMenu: "^sdSelectMenu"
  }
};
